import { http } from '../../http';
import { EchoFilterRequest } from '../filter/types';
import {
    RequestDeleteProtocols,
    RequestGetProtocol,
    RequestPatchProtocol,
    RequestPostAICoachingProtocol,
    RequestPostProtocol,
    RequestPostProtocolApprovalStatus,
    RequestPostProtocolWithoutInteraction,
    RequestProtocolTable,
    RequestUpdateProtocolTableLayout,
    ResponseGetCoachableAgents,
    ResponseGetProtocol,
    ResponsePatchProtocol,
    ResponsePostAICoachingProtocol,
    ResponsePostProtocol,
    ResponsePostProtocolWithoutInteraction,
    ResponseProtocolTable,
} from './protocolModel';

export const getProtocol = async (req: RequestGetProtocol) => {
    return http
        .get<ResponseGetProtocol>(`/protocols/${req.id}`)
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error fetching protocol', error);
            throw error;
        });
};

export const getProtocolTable = async (req: RequestProtocolTable) => {
    return http
        .post<ResponseProtocolTable>(`/protocols/table`, req)
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error fetching protocols', error);
            throw error;
        });
};

export function getProtocolCount(f: EchoFilterRequest): Promise<number> {
    return http
        .post<{ count: number }>('/protocols/count', f)
        .then((res) => res.data.count)
        .catch((err) => {
            console.error('Error fetching protocols', err);
            throw err;
        });
}

export const postProtocol = async (req: RequestPostProtocol) => {
    return http
        .post<ResponsePostProtocol>('/protocols', req)
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error posting protocol', error);
            throw error;
        });
};

export const patchProtocol = async (protocolId: string, req: RequestPatchProtocol) => {
    return http.patch<ResponsePatchProtocol>(`/protocols/${protocolId}`, req).catch((error) => {
        console.error('Error patching protocol', error);
        throw error;
    });
};

export const postAICoachingProtocol = (req: RequestPostAICoachingProtocol) => {
    return http
        .post<ResponsePostAICoachingProtocol>('/protocols/coach-with-ai', req)
        .catch((error) => {
            console.error('Error posting AI coaching protocol', error);
            throw error;
        });
};

export const postProtocolWithoutInteraction = (req: RequestPostProtocolWithoutInteraction) => {
    return http
        .post<ResponsePostProtocolWithoutInteraction>('/protocols/coach-without-interaction', req)
        .catch((error) => {
            console.error('Error posting protocol without interaction', error);
            throw error;
        });
};

export const getCoachableAgents = () => {
    return http
        .get<ResponseGetCoachableAgents>('/protocols/coachable-agents')
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error fetching coachable agents', error);
            throw error;
        });
};

export const postApproveProtocols = async (protocolIds: string[]) => {
    return http
        .post<void>(`/protocols/update-approval-status`, {
            protocolIds,
        })
        .catch((error) => {
            console.error('Error approving protocols', error);
            throw error;
        });
};

export const postProtocolCommentForSection = async (
    protocolId: string,
    protocolSectionId: string,
    text: string
) => {
    return http
        .post(`/protocol-comments/${protocolId}`, {
            protocolSectionId,
            text,
        })
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error posting protocol comment for section', error);
            throw error;
        });
};

export const postProtocolCommentForQuestionAnswer = async (
    protocolId: string,
    protocolQuestionAnswerId: string,
    text: string
) => {
    return http
        .post(`/protocol-comments/${protocolId}`, {
            protocolQuestionAnswerId,
            text,
        })
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error posting protocol comment for question answer', error);
        });
};

export const patchProtocolComment = async (protocolId: string, commentId: string, text: string) => {
    return http
        .patch(`/protocol-comments/${protocolId}/${commentId}`, {
            text,
        })
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error patching protocol comment', error);
        });
};

export const deleteProtocolComment = async (protocolId: string, commentId: string) => {
    return http
        .delete(`/protocol-comments/${protocolId}/${commentId}`)
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error deleting protocol comment', error);
        });
};

export const postProtocolMarkAsRead = async (protocolId: string[]) => {
    return http
        .post<void>(`/protocols/mark-read`, {
            protocolIds: protocolId,
        })
        .catch((error) => {
            console.error('Error marking protocols as read', error);
            throw error;
        });
};

export const updateProtocolTableLayout = async (req: RequestUpdateProtocolTableLayout) => {
    return http
        .patch<RequestUpdateProtocolTableLayout, void>('/protocols/table-layout', req)
        .catch((error) => {
            console.error('Error updating protocol table layout', error);
            throw error;
        });
};

export const updateProtocolApprovalStatus = async (req: RequestPostProtocolApprovalStatus) => {
    return http
        .post<RequestPostProtocolApprovalStatus, void>('/protocols/update-approval-status', req)
        .catch((error) => {
            console.error('Error updating protocol approval status', error);
            throw error;
        });
};

export const deleteProtocols = async (req: RequestDeleteProtocols) => {
    return http.delete<void>('/protocols', { data: req.protocolIds }).catch((error) => {
        console.error('Error deleting protocols', error);
        throw error;
    });
};
